import type { ReactNode } from 'react'
import type { MoonState } from '@/lib/astro/moon'

import { useEffect, useState } from 'react'
import { ArrowUpRight } from 'lucide-react'
import { getMoonState } from '@/lib/astro/moon'
import { FadeLink } from '@/components/generals/fade-link'
import { Wordmark } from '@/components/generals/wordmark'
import { MoonGlyph } from '@/components/generals/moon-glyph'
import { NewsletterForm } from '@/components/generals/newsletter-form'
import { MotionToggle, SoundToggle } from '@/components/generals/toggles'
import { ArcDivider } from '@/components/generals/hairline'
import { OBSERVATORY, formatCoordinate } from '@/constants/observatory'
import { siteConfig } from '@/constants/site'

const columns = [
    {
        title: 'Look up',
        links: [
            { label: 'Sky Tonight', href: '/sky-tonight' },
            { label: 'Planetarium shows', href: '/shows' },
            { label: 'Telescope nights', href: '/telescope-nights' },
            { label: 'Solar System', href: '/solar-system' }
        ]
    },
    {
        title: 'Go further',
        links: [
            { label: 'Research log', href: '/research' },
            { label: 'Learn', href: '/learn' },
            { label: 'Deep sky catalogue', href: '/catalogue' },
            { label: 'Membership', href: '/membership' }
        ]
    },
    {
        title: 'Visit',
        links: [
            { label: 'Getting here', href: '/visit' },
            { label: 'Opening hours', href: '/visit#hours' },
            { label: 'Accessibility', href: '/visit#accessibility' },
            { label: 'Write to us', href: '/visit#contact' }
        ]
    }
]

const MoonLine = (): ReactNode => {
    const [moon, setMoon] = useState<MoonState | null>(null)

    useEffect(() => {
        const update = () => setMoon(getMoonState(new Date()))

        update()
        const timer = window.setInterval(update, 10 * 60 * 1000)

        return () => window.clearInterval(timer)
    }, [])

    return (
        <p className='label-mono flex min-h-5 items-center gap-2.5 text-star-2'>
            {moon ? (
                <>
                    <MoonGlyph fraction={moon.fraction} waxing={moon.waxing} className='size-4' />
                    <span>
                        {`Tonight · ${moon.name} · ${Math.round(moon.fraction * 100)}% lit`}
                    </span>
                </>
            ) : (
                <span className='text-star-3'>
                    Reading the Moon
                </span>
            )}
        </p>
    )
}

export const Footer = (): ReactNode => {
    return (
        <footer className='relative mt-auto border-t border-hair bg-ink'>
            <div className='container-page pb-10 pt-20 md:pt-28'>
                <div className='grid gap-16 lg:grid-cols-12'>
                    <div className='lg:col-span-5'>
                        <p className='label-mono text-brass'>
                            Monthly sky guide
                        </p>
                        <h2 className='display-serif mt-4 text-5xl text-star md:text-6xl'>
                            Clear skies
                        </h2>
                        <p className='mt-5 max-w-md text-star-2'>
                            One letter at the start of each month: what to look for, when the Moon gets out of the way, meteor showers worth staying up for, and first word on events. Nothing else.
                        </p>
                        <NewsletterForm className='mt-8 max-w-md' />
                    </div>

                    <nav aria-label='Footer' className='grid grid-cols-2 gap-10 sm:grid-cols-3 lg:col-span-7 lg:pl-10'>
                        {columns.map(column => (
                            <div key={column.title}>
                                <p className='label-mono text-star-3'>
                                    {column.title}
                                </p>
                                <ul className='mt-5 space-y-3'>
                                    {column.links.map(link => (
                                        <li key={link.href}>
                                            <FadeLink href={link.href} className='text-star-2 transition-colors duration-300 hover:text-star'>
                                                {link.label}
                                            </FadeLink>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        ))}

                        <div className='col-span-2 sm:col-span-3'>
                            <p className='label-mono text-star-3'>
                                Find us
                            </p>
                            <div className='mt-5 grid gap-6 sm:grid-cols-2'>
                                <address className='not-italic text-star-2'>
                                    {OBSERVATORY.addressLines.map(line => (
                                        <span key={line} className='block'>
                                            {line}
                                        </span>
                                    ))}
                                    <span className='data-mono mt-3 block text-star-3'>
                                        {`${formatCoordinate(OBSERVATORY.latitude, 'N', 'S')}  ${formatCoordinate(OBSERVATORY.longitude, 'E', 'W')}`}
                                    </span>
                                </address>
                                <ul className='space-y-3'>
                                    {siteConfig.social.map(social => (
                                        <li key={social.label}>
                                            <a
                                                href={social.href}
                                                target='_blank'
                                                rel='noreferrer'
                                                className='group inline-flex items-center gap-2 text-star-2 transition-colors duration-300 hover:text-star'
                                            >
                                                {social.label}
                                                <ArrowUpRight aria-hidden className='size-3.5 text-brass transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5' strokeWidth={1.25} />
                                            </a>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                    </nav>
                </div>

                <ArcDivider className='mt-20 opacity-80' />

                <div className='mt-8 flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between'>
                    <div className='flex flex-col gap-4 sm:flex-row sm:items-center sm:gap-8'>
                        <FadeLink href='/' aria-label='CosmicLens Observatory, home'>
                            <Wordmark />
                        </FadeLink>
                        <MoonLine />
                    </div>

                    <div className='flex flex-wrap items-center gap-x-8 gap-y-3'>
                        <MotionToggle />
                        <SoundToggle />
                    </div>
                </div>

                <div className='mt-8 flex flex-col gap-3 border-t border-hair pt-6 text-xs text-star-3 md:flex-row md:justify-between'>
                    <p>
                        {`© 2026 ${siteConfig.name}. A dark sky site: please use red torches only.`}
                    </p>
                    <p className='max-w-2xl md:text-right'>
                        Star positions from the HYG catalogue and NASA SVS. Images from NASA, ESA/Hubble and ESO. Planet maps by Solar System Scope (CC BY 4.0). Forecast data from MET Norway.
                    </p>
                </div>
            </div>
        </footer>
    )
}
